// biome-ignore-start lint/style/noCommonJs: next.config.js loads this file with require(), outside the ESM graph
const { z } = require("zod");
// biome-ignore-end lint/style/noCommonJs: next.config.js loads this file with require(), outside the ESM graph

const RUNTIME_IS_SERVER = typeof window === "undefined";

/**
 * Specify your server-side environment variables schema here. This way you can ensure the app isn't
 * built with invalid env vars.
 */
const server = z.object({
  AWS_REGION: z.string().optional(),
  AWS_ACCESS_KEY_ID: z.string().min(1),
  AWS_SECRET_ACCESS_KEY: z.string().min(1),
  CLERK_SECRET_KEY: z.string().startsWith("sk_"),
  CLERK_WEBHOOK_SECRET: z.string().optional(),
  DATABASE_URL: z.string().url(),
  DEBUG_PRISMA: z.enum(["true", "false"]).optional(),
  DOCRAPTOR_API_KEY: z.string().min(1),
  DOCRAPTOR_USE_TEST_SERVICE: z.enum(["true", "false"]),
  LAUNCHDARKLY_SDK_KEY: z.string().min(1),
  INTUIT_CLIENT_ID: z.string().optional(),
  INTUIT_CLIENT_SECRET: z.string().optional(),
  INTUIT_REDIRECT_URI: z.string().optional(),
  PLAID_SANDBOX_CLIENT_ID: z.string().optional(),
  PLAID_SANDBOX_SECRET: z.string().optional(),
  /** Production Plaid app — not provisioned yet; leave unset until go-live. Org lane via PLAID_SANDBOX_MODE. */
  PLAID_PRODUCTION_CLIENT_ID: z.string().optional(),
  PLAID_PRODUCTION_SECRET: z.string().optional(),
  INTEGRATION_ENCRYPTION_KEY: z.string().optional(),
  NODE_ENV: z.enum(["development", "test", "production"]),
  /** When set, a Fathom connection whose API key equals this value serves canned demo meetings (GTM demo orgs). */
  FATHOM_DEMO_API_KEY: z.string().optional(),
  /**
   * Comma-separated org ids that may ADD to the built-in Fathom demo allowlist.
   * Ignored entirely when NODE_ENV is "production" — see fathom-demo.ts.
   */
  FATHOM_DEMO_EXTRA_ORG_IDS: z.string().optional(),
  OPENAI_API_KEY: z.string().optional(),
  OPENROUTER_API_KEY: z.string().optional(),
  /** Together AI — optional; enables getClient("togetherai") for OpenAI-compatible routing. */
  TOGETHER_API_KEY: z.string().optional(),
  DEEPGRAM_API_KEY: z.string().optional(),
  POSTMARK_API_KEY: z.string().min(1),
  /** Live Postmark server token for SMTP outbound. Unset → POSTMARK_API_KEY (often Sandbox locally). */
  POSTMARK_PRODUCTION_API_KEY: z.string().min(1).optional(),
  /** HTTP basic-auth password for POST /api/rest/postmark-inbound. Unset → 403 (fail closed). */
  POSTMARK_INBOUND_WEBHOOK_SECRET: z.string().optional(),
  /** Account-level token for Postmark Data Removal API (GDPR). Unset → erasure throws (fail closed). */
  POSTMARK_ACCOUNT_TOKEN: z.string().optional(),
  /** RequestedBy email for Postmark Data Removal API. Unset → erasure throws (fail closed). */
  POSTMARK_DATA_REMOVAL_REQUESTED_BY: z.string().email().optional(),
  REDIS_URL: z.string().url().default("redis://localhost:6380"),
  ALERT_DAILY_GL_INTEGRITY_WEBHOOK: z.string().optional(),
  ALERT_FX_TIE_OUT_WEBHOOK: z.string().optional(),
  S3_ONBOARDING_IMPORT_BUCKET: z.string().optional(),
  // Optional so a deploy whose env file has not been updated still boots; every bucket operation
  // fails closed with a "not configured" error instead (SC-3562).
  S3_OUTPUT_TEMPLATES_BUCKET: z.string().optional(),
  SQS_ENTITY_EMBEDDING_URL: z.string().optional(),
  SQS_ONBOARDING_IMPORT_URL: z.string().optional(),
  STRIPE_SECRET_KEY: z.string().optional(),
  APP_BASE_URL: z.string().url().optional(),
  STRIPE_PORTAL_CONFIG_ACH_ONLY: z.string().optional(),
  STRIPE_PORTAL_CONFIG_ACH_CARD: z.string().optional(),
  SUPER_ADMIN_CLERK_USER_IDS: z.string().optional(),
  SUPER_ADMIN_EMAILS: z.string().optional(),
  SYSTEM_API_KEY_HASH: z.string().optional(),
});

/**
 * Specify your client-side environment variables schema here. This way you can ensure the app isn't
 * built with invalid env vars. To expose them to the client, prefix them with `NEXT_PUBLIC_`.
 */
const client = z.object({
  NEXT_PUBLIC_AWS_REGION: z.string().min(1),
  NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY: z.string().startsWith("pk_"),
  NEXT_PUBLIC_DOCS_BUCKET: z.string().min(1),
  NEXT_PUBLIC_ENV: z.string().optional(),
  NEXT_PUBLIC_LAUNCHDARKLY_CLIENT_KEY: z.string().min(1),
  NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA: z.string(),
  NEXT_PUBLIC_AG_GRID_LICENSE_KEY: z.string().optional(),
});

/**
 * You can't destruct `process.env` as a regular object in the Next.js edge runtimes (e.g.
 * middlewares) or client-side so we need to destruct manually.
 *
 * @type {Record<keyof z.infer<typeof server> | keyof z.infer<typeof client>, string | undefined>}
 */
const processEnv = {
  AWS_ACCESS_KEY_ID: process.env.AWS_ACCESS_KEY_ID,
  AWS_REGION: process.env.AWS_REGION,
  AWS_SECRET_ACCESS_KEY: process.env.AWS_SECRET_ACCESS_KEY,
  CLERK_SECRET_KEY: process.env.CLERK_SECRET_KEY,
  CLERK_WEBHOOK_SECRET: process.env.CLERK_WEBHOOK_SECRET,
  DATABASE_URL: process.env.DATABASE_URL,
  DEBUG_PRISMA: process.env.DEBUG_PRISMA,
  DOCRAPTOR_API_KEY: process.env.DOCRAPTOR_API_KEY,
  DOCRAPTOR_USE_TEST_SERVICE: process.env.DOCRAPTOR_USE_TEST_SERVICE,
  LAUNCHDARKLY_SDK_KEY: process.env.LAUNCHDARKLY_SDK_KEY,
  INTUIT_CLIENT_ID: process.env.INTUIT_CLIENT_ID,
  INTUIT_CLIENT_SECRET: process.env.INTUIT_CLIENT_SECRET,
  INTUIT_REDIRECT_URI: process.env.INTUIT_REDIRECT_URI,
  PLAID_SANDBOX_CLIENT_ID: process.env.PLAID_SANDBOX_CLIENT_ID,
  PLAID_SANDBOX_SECRET: process.env.PLAID_SANDBOX_SECRET,
  PLAID_PRODUCTION_CLIENT_ID: process.env.PLAID_PRODUCTION_CLIENT_ID,
  PLAID_PRODUCTION_SECRET: process.env.PLAID_PRODUCTION_SECRET,
  INTEGRATION_ENCRYPTION_KEY: process.env.INTEGRATION_ENCRYPTION_KEY,
  NEXT_PUBLIC_AWS_REGION: process.env.NEXT_PUBLIC_AWS_REGION,
  NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY: process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY,
  NEXT_PUBLIC_DOCS_BUCKET: process.env.NEXT_PUBLIC_DOCS_BUCKET,
  NEXT_PUBLIC_ENV: process.env.NEXT_PUBLIC_ENV,
  NEXT_PUBLIC_LAUNCHDARKLY_CLIENT_KEY: process.env.NEXT_PUBLIC_LAUNCHDARKLY_CLIENT_KEY,
  NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA,
  NEXT_PUBLIC_AG_GRID_LICENSE_KEY: Buffer.from(process.env.NEXT_PUBLIC_AG_GRID_LICENSE_KEY ?? "", "base64").toString("utf-8"),
  NODE_ENV: process.env.NODE_ENV,
  FATHOM_DEMO_API_KEY: process.env.FATHOM_DEMO_API_KEY,
  FATHOM_DEMO_EXTRA_ORG_IDS: process.env.FATHOM_DEMO_EXTRA_ORG_IDS,
  OPENAI_API_KEY: process.env.OPENAI_API_KEY,
  OPENROUTER_API_KEY: process.env.OPENROUTER_API_KEY,
  TOGETHER_API_KEY: process.env.TOGETHER_API_KEY,
  DEEPGRAM_API_KEY: process.env.DEEPGRAM_API_KEY,
  POSTMARK_API_KEY: process.env.POSTMARK_API_KEY,
  POSTMARK_PRODUCTION_API_KEY: process.env.POSTMARK_PRODUCTION_API_KEY,
  POSTMARK_INBOUND_WEBHOOK_SECRET: process.env.POSTMARK_INBOUND_WEBHOOK_SECRET,
  POSTMARK_ACCOUNT_TOKEN: process.env.POSTMARK_ACCOUNT_TOKEN,
  POSTMARK_DATA_REMOVAL_REQUESTED_BY: process.env.POSTMARK_DATA_REMOVAL_REQUESTED_BY,
  REDIS_URL: process.env.REDIS_URL,
  ALERT_DAILY_GL_INTEGRITY_WEBHOOK: process.env.ALERT_DAILY_GL_INTEGRITY_WEBHOOK,
  ALERT_FX_TIE_OUT_WEBHOOK: process.env.ALERT_FX_TIE_OUT_WEBHOOK,
  S3_ONBOARDING_IMPORT_BUCKET: process.env.S3_ONBOARDING_IMPORT_BUCKET,
  S3_OUTPUT_TEMPLATES_BUCKET: process.env.S3_OUTPUT_TEMPLATES_BUCKET,
  SQS_ENTITY_EMBEDDING_URL: process.env.SQS_ENTITY_EMBEDDING_URL,
  SQS_ONBOARDING_IMPORT_URL: process.env.SQS_ONBOARDING_IMPORT_URL,
  STRIPE_SECRET_KEY: process.env.STRIPE_SECRET_KEY,
  APP_BASE_URL: process.env.APP_BASE_URL,
  STRIPE_PORTAL_CONFIG_ACH_ONLY: process.env.STRIPE_PORTAL_CONFIG_ACH_ONLY,
  STRIPE_PORTAL_CONFIG_ACH_CARD: process.env.STRIPE_PORTAL_CONFIG_ACH_CARD,
  SUPER_ADMIN_CLERK_USER_IDS: process.env.SUPER_ADMIN_CLERK_USER_IDS,
  SUPER_ADMIN_EMAILS: process.env.SUPER_ADMIN_EMAILS,
  SYSTEM_API_KEY_HASH: process.env.SYSTEM_API_KEY_HASH,
};

// Don't touch the part below
// --------------------------

const merged = server.merge(client).superRefine((data, ctx) => {
  const hasPlaidCredentials = data.PLAID_SANDBOX_CLIENT_ID || data.PLAID_PRODUCTION_CLIENT_ID;
  if (!hasPlaidCredentials) {
    return;
  }
  const key = data.INTEGRATION_ENCRYPTION_KEY;
  if (!key || !/^[0-9a-fA-F]{64}$/.test(key)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["INTEGRATION_ENCRYPTION_KEY"],
      message: "INTEGRATION_ENCRYPTION_KEY must be a 64-character hex string (32 bytes) when Plaid credentials are configured",
    });
  }
});

/** @typedef {z.input<typeof merged>} MergedInput */
/** @typedef {z.infer<typeof merged>} MergedOutput */
/** @typedef {z.SafeParseReturnType<MergedInput, MergedOutput>} MergedSafeParseReturn */

let env = /** @type {MergedOutput} */ (process.env);

const skip = !!process.env.SKIP_ENV_VALIDATION && process.env.SKIP_ENV_VALIDATION !== "false" && process.env.SKIP_ENV_VALIDATION !== "0";
if (!skip) {
  const parsed = /** @type {MergedSafeParseReturn} */ (
    RUNTIME_IS_SERVER
      ? merged.safeParse(processEnv) // on server we can validate all env vars
      : client.safeParse(processEnv) // on client we can only validate the ones that are exposed
  );

  if (parsed.success === false) {
    // biome-ignore-start lint/suspicious/noConsole: carried over from the ESLint suppression
    console.error("❌ Invalid environment variables:", parsed.error.flatten().fieldErrors);
    // biome-ignore-end lint/suspicious/noConsole: carried over from the ESLint suppression
    throw new Error("Invalid environment variables");
  }

  env = new Proxy(parsed.data, {
    get(target, prop) {
      if (typeof prop !== "string") {
        return undefined;
      }
      // Throw a descriptive error if a server-side env var is accessed on the client
      // Otherwise it would just be returning `undefined` and be annoying to debug
      if (!RUNTIME_IS_SERVER && !prop.startsWith("NEXT_PUBLIC_")) {
        throw new Error(
          process.env.NODE_ENV === "production"
            ? "❌ Attempted to access a server-side environment variable on the client"
            : `❌ Attempted to access server-side environment variable '${prop}' on the client`,
        );
      }
      return target[/** @type {keyof typeof target} */ (prop)];
    },
  });
}

const CURRENT_ENV = (env.NEXT_PUBLIC_ENV ?? "").trim();

const IS_LOCALHOST = CURRENT_ENV === "localhost";
const IS_DEVELOPMENT = CURRENT_ENV === "development";
const IS_PREVIEW = CURRENT_ENV === "preview";
const IS_PR = CURRENT_ENV === "pr";
const IS_PRODUCTION = CURRENT_ENV === "production";
const IS_SANDBOX = CURRENT_ENV === "sandbox";

const IS_PROD = IS_PRODUCTION || IS_SANDBOX;
const IS_DEV = !IS_PROD;

const S3_DOMAIN = `https://s3.${env.NEXT_PUBLIC_AWS_REGION}.amazonaws.com`;
const BUCKET_URL = `${S3_DOMAIN}/${env.NEXT_PUBLIC_DOCS_BUCKET}`;
const BUCKET_DOMAIN = `https://${env.NEXT_PUBLIC_DOCS_BUCKET}.s3.${env.NEXT_PUBLIC_AWS_REGION}.amazonaws.com`;

// biome-ignore-start lint/style/noCommonJs: next.config.js loads this file with require(), outside the ESM graph
module.exports = {
  env,

  S3_DOMAIN,
  BUCKET_URL,
  BUCKET_DOMAIN,

  RUNTIME_IS_SERVER,

  CURRENT_ENV,
  IS_LOCALHOST,
  IS_DEVELOPMENT,
  IS_PREVIEW,
  IS_PRODUCTION,
  IS_SANDBOX,
  IS_DEV,
  IS_PROD,
  IS_PR,

  GLOBAL_BANNER_ENABLED: IS_PREVIEW || IS_SANDBOX || IS_PR,
};
// biome-ignore-end lint/style/noCommonJs: next.config.js loads this file with require(), outside the ESM graph
